import {
  Component,
  ComponentRef,
  DestroyRef,
  inject,
  OnInit,
  signal,
  Type,
  ViewChild,
  ViewContainerRef,
  ChangeDetectionStrategy
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { form } from "@angular/forms/signals";
import { MatButtonModule } from "@angular/material/button";
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { startWith } from "rxjs";

export interface DialogData {
  title: string;
  submitLabel: string;
  closeLabel: string;
  formComponent: Type<any>;
  formInput: any;
  disabled: boolean;
}

@Component({
  selector: "lib-form-dialog-component",
  imports: [MatDialogModule, MatButtonModule],
  templateUrl: "./form-dialog-component.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./form-dialog-component.component.css",
})
export class FormDialogComponent implements OnInit {
  #destroyRef = inject(DestroyRef);

  @ViewChild("formComponentRef", { read: ViewContainerRef, static: true }) vcr!: ViewContainerRef;
  formComponentRef!: ComponentRef<any>;

  dialogData = inject<DialogData>(MAT_DIALOG_DATA);
  dialogRef = inject(MatDialogRef<FormDialogComponent>);

  $formIsInvalid = signal<boolean>(true);
  $formIsDirty = signal<boolean>(false);

  // For opening confirmation dialog
  #matDialog = inject(MatDialog);

  ngOnInit() {
    // Manage the Backdrop Click
    this.dialogRef.disableClose = true;
    this.dialogRef
      .backdropClick()
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => this.handleCancel());

    this.formComponentRef = this.vcr.createComponent(this.dialogData.formComponent);

    // Use setInput for Signal-based inputs ($input and $disabled)
    if (this.dialogData.formInput) {
      this.formComponentRef.setInput("input", this.dialogData.formInput);
    }
    if (this.dialogData.disabled !== undefined) {
      this.formComponentRef.setInput("disabled", this.dialogData.disabled);
    }
    const formGroup = this.formComponentRef.instance.form?.formGroup;
    if (formGroup) {
      formGroup.statusChanges.pipe(startWith(formGroup.status), takeUntilDestroyed(this.#destroyRef)).subscribe(() => {
        this.$formIsInvalid.set(formGroup.invalid);
      });

      formGroup.valueChanges.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => {
        this.$formIsDirty.set(formGroup.dirty);
      });
    }
  }

  handleCancel() {
    const form = this.formComponentRef?.instance.form?.formGroup;

    if (form?.dirty) {
      // Open the confirmation dialog
      const confirmRef = this.#matDialog.open(ConfirmDiscardDialog, {
        width: "350px",
        disableClose: true, // Force them to choose an option
      });

      confirmRef.afterClosed().subscribe((shouldDiscard: boolean) => {
        if (shouldDiscard) {
          this.dialogRef.close();
        }
      });
    } else {
      this.dialogRef.close();
    }
  }

  handleSave() {
    const instance = this.formComponentRef?.instance;
    if (instance.form?.formGroup?.valid) {
      // Trigger your manualSave$ subject
      instance.manualSave$.next();
      this.dialogRef.close(instance.form.formGroup.value);
    }
  }
}

/**
 * Form Dialog Confirm Discard Dialog
 */
@Component({
  selector: "lib-confirm-discard-dialog",
  imports: [MatDialogModule, MatButtonModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <h2 mat-dialog-title>Discard Changes?</h2>
    <mat-dialog-content> You have unsaved changes. If you leave now, your progress will be lost. </mat-dialog-content>
    <mat-dialog-actions>
      <button
        mat-button
        [mat-dialog-close]="false"
      >
        Keep Editing
      </button>
      <button
        mat-raised-button
        color="warn"
        [mat-dialog-close]="true"
      >
        Discard
      </button>
    </mat-dialog-actions>
  `,
})
export class ConfirmDiscardDialog {}
